import { BLUE_400, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { Box, Stack, Typography } from '@mui/material'

import { FC } from 'react'
import { SkyStyleOption } from './EnhancePanel.component'
import { useTranslation } from 'react-i18next'

interface SkyStyleSelectorProps {
  skyStyles: SkyStyleOption[]
  selectedStyle: string | null
  onStyleSelect: (styleId: string) => void
  isDisabled?: boolean
}

/**
 * Grid of sky replacement style options (No cloud, Few clouds, Cloudy).
 * Each option shows a preview thumbnail and label.
 */
export const SkyStyleSelector: FC<SkyStyleSelectorProps> = ({ skyStyles, selectedStyle, onStyleSelect, isDisabled = false }) => {
  const { t } = useTranslation(['photo_studio_editing'])

  return (
    <Stack gap={0.8}>
      <Typography variant="text-md" fontWeight={550} color={GRAY_900} sx={{ fontSize: '1.6rem', lineHeight: 1.5 }}>
        {t('enhance.sky_style_title')}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '1.2rem',
        }}
      >
        {skyStyles.map((style) => {
          const isSelected = selectedStyle === style.id

          return (
            <Box
              key={style.id}
              onClick={() => !isDisabled && onStyleSelect(style.id)}
              sx={{
                cursor: isDisabled ? 'not-allowed' : 'pointer',
              }}
            >
              <Stack gap={0.6}>
                <Box
                  sx={{
                    height: '9.6rem',
                    borderRadius: '.8rem',
                    overflow: 'hidden',
                    border: isSelected ? `3px solid ${BLUE_400}` : 'none',
                    position: 'relative',
                  }}
                >
                  {style.previewImage ? (
                    <Box
                      component="img"
                      src={style.previewImage}
                      alt={style.label}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: '#E3F0FC',
                      }}
                    />
                  )}
                </Box>
                <Typography
                  variant="text-sm"
                  fontWeight={isSelected ? 450 : 350}
                  color={isSelected ? GRAY_900 : GRAY_700}
                  sx={{
                    fontSize: '1.4rem',
                    lineHeight: 1.4,
                    textAlign: 'center',
                  }}
                >
                  {style.label}
                </Typography>
              </Stack>
            </Box>
          )
        })}
      </Box>
    </Stack>
  )
}
